import { Inject, Injectable, OpaqueToken } from "@angular/core";
import { JwtHttpClientService } from "../../http/jwt-http-client.service";
import { AbstractHttpService } from "../../http/abstract-http.service";
import { Headers } from "@angular/http";
import { ConfigurationService } from "../../configuration/configuration.service";
import { ResourceModule } from "../resource.module";
import { Observable } from "rxjs";
export var SyncMode;
(function (SyncMode) {
    SyncMode[SyncMode["CREATE_IF_MISSING"] = "CREATE_IF_MISSING"] = "CREATE_IF_MISSING";
    SyncMode[SyncMode["SHALLOW_SYNC"] = "SHALLOW_SYNC"] = "SHALLOW_SYNC";
    SyncMode[SyncMode["DEEP_SYNC"] = "DEEP_SYNC"] = "DEEP_SYNC";
})(SyncMode || (SyncMode = {}));
export class SyncOptions {
    constructor() {
        this.syncMode = SyncMode.SHALLOW_SYNC;
        this.syncMenuItems = true;
        this.addNewSellablesAsNotSoldHere = false;
    }
}
export class EntitySyncRequest {
    constructor(identifier, entityName) {
        this.identifier = identifier;
        this.entityName = entityName;
        this.targetClientIds = [];
        this.syncOptions = new SyncOptions();
    }
}
/**
 * Generic service for accessing an entity resource from the server.
 *
 * The #ENTITY_RESOURCE must be registered as a provider in the Angular dependency injection to specify the resource
 * name. This will form part of the URL path
 *
 * Errors are returned as a #DataClientErrorMessage
 */
export class EntityService extends AbstractHttpService {
    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #ENTITY_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     * @param entityNames Entity names for the APP
     * @param resource Resource name that forms part of the URL
     * @param version Resource version that forms part of the URL
     */
    constructor(http, configService, nameSpace, entityNames, resource, version, useAsync) {
        super();
        this.http = http;
        this.configService = configService;
        this.nameSpace = nameSpace;
        this.entityNames = entityNames;
        this.resource = resource;
        this.version = version;
        this.useAsync = useAsync;
        if (entityNames && entityNames.indexOf(resource) < 0) {
            throw new Error("Entity " + resource + " is not supported.");
        }
        let url = EntityService.URL_PREFIX + nameSpace + "/" + resource;
        if (version) {
            url = url + ".v" + version;
        }
        url = url + "/";
        this.url = url;
    }
    getNameSpace() {
        return this.nameSpace;
    }
    getResource() {
        return this.resource;
    }
    /**
     * Retrieves an entity by a unique identifier
     * @param identifier Primary Key
     * @param params
     * @returns {Promise<any>} JSON object representation of the entity
     */
    getEntity(identifier, params) {
        return this.http.get(this.url + identifier, { params: params })
            .map((response) => response.json() || {})
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Retrieves an entity by a unique identifier
     * @param identifier Primary Key
     * @returns {Promise<any>} JSON object representation of the entity
     */
    cloneEntity(identifier) {
        if (this.useAsync) {
            return this.handleAsync(this.http.get(this.url + identifier, {
                params: { clone: "true" },
                headers: new Headers({ "async": "true" })
            }));
        }
        else {
            return this.http.get(this.url + identifier, { params: { clone: "true" } })
                .map((response) => response.json() || {})
                .catch((response) => this.handleServiceError(response))
                .toPromise();
        }
    }
    /**
     * Retrieves a nested collection of an entity by a path
     * @returns {Promise<any>} JSON array representation of the collection
     * @param collectionPath
     */
    getCollection(collectionPath) {
        return this.http.get(this.url + collectionPath)
            .map((response) => response.json() || {})
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Retrieves a nested component of an entity by a path
     * @returns {Promise<any>} JSON object representation of the component
     * @param componentPath
     */
    getComponent(componentPath) {
        return this.http.get(this.url + componentPath)
            .map((response) => response.json() || {})
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Creates an entity
     * @param entityData
     * @returns {Promise<any>} JSON object representation of the saved entity
     */
    createEntity(entityData) {
        if (this.useAsync) {
            return this.handleAsync(this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData, { headers: new Headers({ "async": "true" }) }));
        }
        else {
            return this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData)
                .map((response) => response.json() || {})
                .catch((response) => this.handleServiceError(response))
                .toPromise();
        }
    }
    /**
     * Updates an entity
     * @param entityData
     * @returns {Promise<any>} JSON object representation of the saved entity
     */
    updateEntity(entityData) {
        if (this.useAsync) {
            return this.handleAsync(this.http.put(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData, { headers: new Headers({ "async": "true" }) }));
        }
        else {
            return this.http.put(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData)
                .map((response) => response.json() || {})
                .catch((response) => this.handleServiceError(response))
                .toPromise();
        }
    }
    handleAsync(serviceObservable) {
        return new Promise((resolve, reject) => {
            serviceObservable.map((response) => {
                if (response.status == 202 && response.headers.has("processId")) {
                    this.waitPendingProcess(resolve, reject, response.headers.get("processId"));
                }
                else {
                    resolve(response.json() || {});
                }
            }).catch((response) => this.handleServiceError(response))
                .toPromise()
                .catch(reason => reject(reason));
        });
    }
    waitPendingProcess(resolve, reject, processId) {
        Observable.timer(5000).subscribe(() => {
            let url = EntityService.URL_PENDING_PREFIX + processId;
            this.http.get(url)
                .catch((response) => this.handleServiceError(response))
                .toPromise()
                .then(response => {
                if (response.status == 202) {
                    this.waitPendingProcess(resolve, reject, processId);
                }
                else {
                    resolve(response.json() || {});
                }
            })
                .catch(reason => {
                reject(reason);
            });
        });
    }
    /**
     * Delete an entity
     * @param identifier
     * @returns {Promise<R>) returns no errors if successful
     */
    deleteEntity(identifier) {
        return this.http.delete(this.url + identifier, { params: { action: "delete" } })
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Discontinue an entity
     * @param identifier
     * @returns {Promise<R>) returns no errors if successful
     */
    discontinueEntity(identifier) {
        return this.http.delete(this.url + identifier, { params: { action: "discontinue" } })
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Recontinue an entity
     * @param identifier
     * @returns {Promise<any>) returns no errors if successful
     */
    recontinueEntity(identifier) {
        return this.http.delete(this.url + identifier, { params: { action: "recontinue" } })
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Initiates a sync of an entity
     * @param entitySyncRequest
     * @returns {Promise<any>) returns no errors if successful
     */
    syncEntity(entitySyncRequest) {
        return this.http.post(this.url + "sync", entitySyncRequest)
            .map((response) => response.json() || {})
            .catch((response) => this.handleServiceError(response))
            .toPromise();
    }
    /**
     * Indicates that the authenticated user has permission to create the entity
     * @returns {Promise<boolean>}
     */
    canCreateEntity() {
        return EntityService.canCreateEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    /**
     * Indicates that the authenticated user has permission to retrieve the entity
     * @returns {Promise<boolean>}
     */
    canGetEntity() {
        return EntityService.canGetEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    /**
     * Indicates that the authenticated user has permission to update the entity
     * @returns {Promise<boolean>}
     */
    canUpdateEntity() {
        return EntityService.canUpdateEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    /**
     * Indicates that the authenticated user has permission to delete the entity
     * @returns {Promise<boolean>}
     */
    canDeleteEntity() {
        return EntityService.canDeleteEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    /**
     * Indicates that the authenticated user has permission to discontinue/recontinue the entity
     * @returns {Promise<boolean>}
     */
    canDiscontinueEntity() {
        return EntityService.canDiscontinueEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    /**
     * Indicates that the authenticated user has permission to sync the entity to another instance in the group
     * @returns {Promise<boolean>}
     */
    canSyncEntity() {
        return EntityService.canSyncEntity(this.configService, this.nameSpace + "/" + this.resource);
    }
    static canCreateEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "create")) : false);
    }
    static canGetEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "view")) : false);
    }
    static canUpdateEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "update")) : false);
    }
    static canDeleteEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "delete")) : false);
    }
    static canDiscontinueEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "update")) : false);
    }
    static canSyncEntity(configService, resource) {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "sync")) : false);
    }
}
/**
 * Static URL prefix for all server side calls
 * @type {string}
 */
EntityService.URL_PREFIX = '/dataserver/entity/';
/**
 * Provider lookup key alias that is used during dependency injection
 * @type {OpaqueToken}
 */
EntityService.ENTITY_RESOURCE = new OpaqueToken('entityResource');
/**
 * Provider lookup key alias that is used during dependency injection
 * @type {OpaqueToken}
 */
EntityService.RESOURCE_VERSION = new OpaqueToken('resourceVersion');
/**
 * Static URL prefix for getting a pending process result
 * @type {string}
 */
EntityService.URL_PENDING_PREFIX = '/dataserver/entity/pendingProcess/';
EntityService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
EntityService.ctorParameters = () => [
    { type: JwtHttpClientService, },
    { type: ConfigurationService, },
    { type: undefined, decorators: [{ type: Inject, args: [ResourceModule.RESOURCE_NAMESPACE_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ResourceModule.ENTITY_NAMES_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [EntityService.ENTITY_RESOURCE,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [EntityService.RESOURCE_VERSION,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [ResourceModule.RESOURCE_USE_ASYNC,] },] },
];
//# sourceMappingURL=entity.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/resource/entity/entity.service.js
// module id = 420
// module chunks = 1